import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import SegmentedControl from '@/components/molecules/SegmentedControl';
import { useCageScopedSearch } from '@/contexts/cage-filter-context';

import styles from './HealthWoundsBodySubTabBar.module.scss';

export interface HealthWoundsBodySubTabBarProps {
    trailing?: ReactNode;
}

export default function HealthWoundsBodySubTabBar(props: HealthWoundsBodySubTabBarProps) {
    const { trailing } = props;
    const { t } = useTranslation('healthModule');
    const location = useLocation();
    const search = useCageScopedSearch();

    const bodyBaseUrl = useMemo(
        () => location.pathname.replace(/\/(laksvel|insight|graphs)$/, ''),
        [location.pathname]
    );

    const laksvelPath = `${bodyBaseUrl}/laksvel`;
    const insightPath = `${bodyBaseUrl}/insight`;
    const graphsPath = `${bodyBaseUrl}/graphs`;

    const tabs = [
        { value: 'laksvel', label: t('wounds.laksvel'), path: laksvelPath },
        { value: 'insight', label: t('wounds.insight'), path: insightPath },
        { value: 'graphs', label: t('wounds.graphs'), path: graphsPath },
    ];
    const activeTab = tabs.find((tab) => location.pathname === tab.path)?.value;
    // The links carry the cage-scoped params (`fg`); the active tab is matched on the path.
    const options = tabs.map(({ path, ...tab }) => ({ ...tab, to: `${path}${search}` }));

    return (
        <div className={styles.row}>
            <SegmentedControl
                className={styles.segmented}
                aria-label={t('routes.bodyWounds')}
                fullWidth
                options={options}
                value={activeTab}
            />
            {trailing ? <div className={styles.trailing}>{trailing}</div> : null}
        </div>
    );
}
